import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Header from "./Header";
import Footer from "./Footer";
import tokenomicsShellStyle from "../styles/tokenomicsShellStyle";
import SeoHead from "../components/SeoHead";

const slugify = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const getText = (node) => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getText).join("");
  if (node?.props?.children) return getText(node.props.children);
  return "";
};

const cleanMarkdown = (raw) =>
  raw
    .replace(/^###\s*$/gm, "")
    .replace(/(\d+)\\\./g, "$1.")
    .replace(
      /[\u{1F300}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F1E0}-\u{1F1FF}\u{200D}\u{FE0F}\u{20E3}]/gu,
      ""
    )
    .replace(/[✅🌐🔹🚀🔥🎲📈⭐✨🎯💡⚠️❌✔️⃣]/g, "")
    .replace(/\n{3,}/g, "\n\n");

const CHAPTER_START =
  /^(#{1,3})\s+\*{0,2}(\d+)\.\s+(.+?)\*{0,2}\s*$/;

const parseChapters = (markdown) => {
  const lines = markdown.split("\n");
  const chapters = [];
  let current = null;
  let nextNum = 1;

  lines.forEach((line) => {
    const match = CHAPTER_START.exec(line.trim());
    if (match) {
      const num = Number(match[2]);
      const title = match[3].replace(/\*\*/g, "").trim();
      // Only accept chapters 1..13 in ascending order once each
      if (num === nextNum && nextNum <= 13) {
        if (current) chapters.push(current);
        current = {
          num: String(num),
          title: `${num}. ${title}`,
          shortTitle: title,
          id: slugify(`${num}-${title}`),
          bodyLines: [`### **${num}. ${title}**`],
        };
        nextNum += 1;
        return;
      }
    }
    if (current) current.bodyLines.push(line);
  });

  if (current) chapters.push(current);

  return chapters.map((c) => ({
    ...c,
    content: c.bodyLines.join("\n").trim(),
  }));
};

const Whitepaper = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [markdown, setMarkdown] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch("/whitepaper.md")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load whitepaper");
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setMarkdown(cleanMarkdown(text));
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load the whitepaper right now.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chapters = useMemo(
    () => (markdown ? parseChapters(markdown) : []),
    [markdown]
  );

  const activeIndex = useMemo(() => {
    if (!chapters.length) return -1;
    if (!sectionId) return 0;
    const idx = chapters.findIndex((c) => c.id === sectionId);
    return idx >= 0 ? idx : 0;
  }, [chapters, sectionId]);

  const activeChapter = activeIndex >= 0 ? chapters[activeIndex] : null;

  useEffect(() => {
    if (!chapters.length) return;
    if (!sectionId) {
      navigate(`/whitepaper/${chapters[0].id}`, { replace: true });
    } else if (activeIndex === 0 && sectionId !== chapters[0].id && !chapters.some((c) => c.id === sectionId)) {
      navigate(`/whitepaper/${chapters[0].id}`, { replace: true });
    }
  }, [chapters, sectionId, activeIndex, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [sectionId]);

  const headingComponents = {
    h1: ({ children }) => (
      <h1 className="wp-h1">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 id={slugify(getText(children))} className="wp-h2">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 id={slugify(getText(children))} className="wp-h3">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 id={slugify(getText(children))} className="wp-h4">
        {children}
      </h4>
    ),
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    ),
  };

  const prev = activeIndex > 0 ? chapters[activeIndex - 1] : null;
  const next =
    activeIndex >= 0 && activeIndex < chapters.length - 1
      ? chapters[activeIndex + 1]
      : null;

  const seoTitle = activeChapter
    ? `${activeChapter.title} | Atheriam Whitepaper`
    : "Atheriam Whitepaper - Official Document";
  const seoPath = activeChapter
    ? `/whitepaper/${activeChapter.id}`
    : "/whitepaper";

  return (
    <div className="tokenomics-shell" style={tokenomicsShellStyle}>
      <SeoHead
        title={seoTitle}
        description="Read the official Atheriam whitepaper - Layer 1 blockchain for Agentic AI-powered Web3 gaming, tokenomics, and the $ATH ecosystem."
        path={seoPath}
        type="article"
      />
      <Header />
      <main className="whitepaper-page">
        <section className="whitepaper-hero">
          <p className="whitepaper-hero__eyebrow">Atheriam · Official Document</p>
          <h1 className="whitepaper-hero__title">
            Atheriam <span>Whitepaper</span>
          </h1>
          <p className="whitepaper-hero__sub">
            Browse chapter by chapter - pick a section from the contents menu.
          </p>
          <div className="whitepaper-hero__actions">
            <Link to="/tokenomics" className="landing-btn-secondary">
              View Tokenomics
            </Link>
            <Link to="/ico" className="landing-btn-primary">
              Go to ICO
            </Link>
          </div>
        </section>

        <section className="whitepaper-layout" id="whitepaper-content">
          <aside className="whitepaper-toc">
            <p className="whitepaper-toc__label">Contents</p>
            <nav>
              {chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/whitepaper/${chapter.id}`}
                  className={`whitepaper-toc__link level-2 ${
                    activeChapter?.id === chapter.id ? "is-active" : ""
                  }`}
                >
                  {chapter.title}
                </Link>
              ))}
            </nav>
          </aside>

          <article className="whitepaper-doc">
            {error && <p className="whitepaper-error">{error}</p>}
            {!error && !markdown && (
              <p className="whitepaper-loading">Loading whitepaper…</p>
            )}
            {activeChapter && (
              <>
                <p className="whitepaper-doc__chapter">
                  Chapter {activeChapter.num} of {chapters.length}
                </p>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={headingComponents}
                >
                  {activeChapter.content}
                </ReactMarkdown>

                <div className="whitepaper-pager">
                  {prev ? (
                    <Link
                      to={`/whitepaper/${prev.id}`}
                      className="whitepaper-pager__btn"
                    >
                      Previous
                      <span>{prev.shortTitle}</span>
                    </Link>
                  ) : (
                    <span />
                  )}
                  {next ? (
                    <Link
                      to={`/whitepaper/${next.id}`}
                      className="whitepaper-pager__btn whitepaper-pager__btn--next"
                    >
                      Next
                      <span>{next.shortTitle}</span>
                    </Link>
                  ) : (
                    <span />
                  )}
                </div>
              </>
            )}
          </article>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Whitepaper;
